/**
 * BookingTicketCard Component
 *
 * Composed component that combines TicketCard with BookingSummaryDisplay.
 * Contains all the page-specific pricing and action button logic.
 *
 * This is used specifically in the BookingTimeStep and is NOT a reusable component.
 * The reusable part is BookingSummaryDisplay which can be used elsewhere.
 */

import {
  TicketCard,
  TicketDivider,
} from "@butter/ui/patterns";
import {
  BookingSummaryDisplay,
  PriceDisplay,
} from "@butter/ui/domains/booking";
import { TicketCtaButton } from "../../shared/TicketCtaButton";
import { formatDuration } from "../../shared/formatDuration";
import { useBooking } from "~/services/internalApi/booking";
import { useClubQuery } from "~/services/internalApi/club/hooks/useClubQuery";
import { CLUB_NO_ACCESS_LABEL, CLUB_NO_ACCESS_MESSAGE } from "~/services/internalApi/club";
import { timeStringToMinutes, formatTime } from "@butter/library/datetime";
import { format, parseISO } from 'date-fns';
import { isDevelopment } from "~/config/env";
import type { ApiValidateBookingResponse } from "@lgc/database/types/domains/subscription";
import type { ApiBookingQuotePromotion } from "@lgc/database/types/domains/booking";

/**
 * Reasons the Continue/Confirm action is blocked outright (distinct from the
 * "log in / get membership" prompt driven by `userCanBook`). Typed so callers
 * can't pass an unhandled reason and the copy stays in one place.
 */
export type BookingActionBlock = 'no-club-access';

const BOOKING_BLOCK_COPY: Record<BookingActionBlock, { label: string; message: string }> = {
  'no-club-access': {
    label: CLUB_NO_ACCESS_LABEL,
    message: CLUB_NO_ACCESS_MESSAGE,
  },
};

export interface BookingTicketCardProps {
  /** Whether to show pricing information */
  showPricing?: boolean;
  /** Callback when "Continue" button is clicked */
  onContinue?: () => void;
  /** Callback when "Confirm Booking" button is clicked (for free bookings) */
  onConfirmBooking?: () => void;
  /**
   * Callback when the visitor needs to log in / get membership before they can
   * book (i.e. `userCanBook` is false and there's no hard block). When provided,
   * the CTA stays enabled and invokes this instead of the normal continue flow.
   */
  onRequiresAccess?: () => void;
  /** Whether the visitor is logged in — drives the "needs access" CTA copy
   * ("Get Membership" when logged in, "Log In or Get Membership" when not). */
  isAuthenticated?: boolean;
  /** Whether the Continue button should be enabled */
  canContinue?: boolean;
  /** Whether booking creation is in progress */
  isCreatingBooking?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Validation data from booking validation API */
  validationData?: ApiValidateBookingResponse["payload"] | null;
  /**
   * The server's verdict on the flow's promo code (a booking promo link, or one
   * typed earlier). An APPLIED code renders as a summary pill; refusals are the
   * payment step's `PromoCodeDisclosure`'s to explain.
   */
  promotion?: ApiBookingQuotePromotion | null;
  /** Whether user has an active subscription */
  hasActiveSubscription?: boolean;
  /** Whether user can book (used to show restriction message) */
  userCanBook?: boolean;
  /** Hard block on the action (e.g. viewing a club the member isn't enrolled in). */
  blockedReason?: BookingActionBlock | null;
  /** Number of guests on the booking (from booking context) */
  guestCount?: number;
  /**
   * Render the mobile action as a floating CTA portaled to <body> (fixed to
   * the viewport bottom). Only the mobile bay-substep instance should set this
   * — the desktop summary card must NOT, or its portaled button leaks onto
   * mobile and overlaps the "Select a Bay" button.
   */
  floatingMobileCta?: boolean;
}



export function BookingTicketCard({
  showPricing = true,
  onContinue,
  onConfirmBooking,
  onRequiresAccess,
  isAuthenticated = false,
  canContinue = true,
  isCreatingBooking = false,
  className = "",
  validationData,
  promotion = null,
  hasActiveSubscription = false,
  userCanBook = true,
  blockedReason = null,
  guestCount = 0,
  floatingMobileCta = false,
}: BookingTicketCardProps) {
  // Get booking selections and available bays from context
  const { selectedDate, selectedStartTime, selectedEndTime, selectedBayId, availableBays } =
    useBooking();

  // Fetch club data
  const { data: club } = useClubQuery();

  // Get available bays from context (fetched by parent BookingTimeStep)
  // No need to fetch again - this prevents duplicate API calls

  // Check if we have all required data for display
  const hasRequiredData = !!(
    selectedDate &&
    selectedStartTime &&
    selectedEndTime &&
    selectedBayId &&
    club
  );

  // If no required data, render empty state
  if (!hasRequiredData) {
    return (
      <TicketCard borderColor="var(--theme-100)" className={className}>
        <div className="p-6 opacity-50">
          <h2 className="mb-6 text-sm font-medium text-fg-primary">
            Booking Summary
          </h2>
          <p className="text-sm text-fg-quaternary">
            Select a time and bay to see your booking summary
          </p>
        </div>
      </TicketCard>
    );
  }

  // Get selected bay details
  const selectedBay = availableBays?.items.find((b) => b.id === selectedBayId);
  const bayName = selectedBay?.name || `Bay #${selectedBayId}`;

  // Calculate duration
  const startMinutes = timeStringToMinutes(selectedStartTime);
  const endMinutes = timeStringToMinutes(selectedEndTime);
  const durationMinutes = (endMinutes || 0) - (startMinutes || 0);

  const formattedDate = format(parseISO(selectedDate), "EEEE, d MMMM, yyyy");

  // Format time range with AM/PM
  // '24:00' (end-of-day sentinel) is handled correctly by formatTime → shows "12:00 AM"
  const formattedStartTime = formatTime(selectedStartTime);
  const formattedEndTime = formatTime(selectedEndTime);
  const timeRange = `${formattedStartTime} - ${formattedEndTime}`;

  // The premium pill shows exactly when the server charged one: the quote carries a
  // `bay_premium` line. Who owes a premium (no plan, an unrestricted bay, a bay on
  // the club rate rather than its own) is the server's call, and re-deriving it
  // here from the bay row advertised surcharges nobody was charged.
  //
  // Waiting for the quote also means a failed quote shows nothing, which is the
  // right direction to fail — never claim a surcharge we could not confirm.
  //
  // Amount deliberately omitted: the priced breakdown is on the next step, and a
  // per-hour figure here next to a per-booking total invites the wrong sum.
  const showPremiumNote = (validationData?.fees ?? []).some((f) => f.type === 'bay_premium');
  const premiumNote = showPremiumNote ? 'Bay Premium' : undefined;

  // Calculate price
  let calculatedPrice: number | null = null;
  // Whether the club's peak pricing raised the rate for this booking. The
  // server's verdict — the browser cannot resolve a pricing mode. Rendered as a
  // chip in local development only (see `peakNote` below): whether members
  // should see a "peak" label at all is still a product call, and the price it
  // would explain is already on this card.
  let isPeakRate = false;
  let isFree = false;
  let hasOverage = false; // Track if this is an overage booking
  // The no-plan price is the quote's total, tax included, so the card must not caption it
  // "Taxes not included". Guest fees under a plan are pre-tax and keep the caption.
  let priceIncludesTax = false;

  if (showPricing) {
    // Guest fees as the server priced them. A bay that charges per person has
    // none (its guests pay the bay's rate), which local math cannot know.
    const guestFeeDollars = (validationData?.fees ?? [])
      .filter((f) => f.type === 'guest_fee')
      .reduce((sum, f) => sum + f.baseAmount, 0);

    // If user has active subscription, use validation data
    if (hasActiveSubscription && validationData) {
      hasOverage = validationData.overage?.hasOverage || false;

      if (hasOverage) {
        // User has overage - hide price until confirmation page
        // Product requirement: Don't show overage cost in booking summary
        // to avoid confusion for members who expect free bookings
        calculatedPrice = null; // Hide price
      } else if (guestFeeDollars > 0) {
        // Base booking covered by membership, but guest fees still apply
        calculatedPrice = guestFeeDollars;
        isFree = false;
      } else {
        // Fully covered by subscription — no guest fees either
        isFree = true;
        calculatedPrice = 0;
      }
    }
    // Booker with no plan — the server priced it, including any per-bay premium.
    // There is no local fallback: the browser can read `bays.premium_cents` but not
    // who owes it or the club's pricing mode, so an estimate here under-quoted a premium bay by
    // exactly the surcharge. Until the quote lands the price simply stays null,
    // which this card already renders as no price rather than as free.
    else if (validationData?.totalCost) {
      calculatedPrice = validationData.totalCost.total;
      priceIncludesTax = true;
      isPeakRate = (validationData.fees ?? []).some(
        (f) => f.details?.detailType === 'time' && f.details.isPeakRate === true,
      );
    }
  }

  // A hard block (e.g. not enrolled at this club) disables the action entirely;
  // the explanatory copy renders beneath the button.
  const block = blockedReason ? BOOKING_BLOCK_COPY[blockedReason] : null;

  // Visitor simply needs to log in / get membership (no hard block). This stays
  // a clickable CTA that opens sign-in, rather than a dead disabled button.
  const needsAccess = !userCanBook && !block;

  // Determine which action to take.
  // Overage bookings should continue to payment/confirmation (not free booking flow).
  const handleAction = needsAccess
    ? () => onRequiresAccess?.()
    : isFree && !hasOverage && onConfirmBooking
      ? onConfirmBooking
      : onContinue;

  // Button label and state
  let buttonLabel =
    isFree && !hasOverage ? "Confirm Booking" : "Continue to Confirmation";
  let ariaLabel =
    isFree && !hasOverage
      ? "Confirm booking"
      : "Continue to booking confirmation";

  if (needsAccess) {
    buttonLabel = isAuthenticated
      ? "Get Membership to Book"
      : "Log In or Get Membership to Book";
    ariaLabel = isAuthenticated
      ? "Get a membership to book"
      : "Log in or purchase membership to book";
  }

  if (block) {
    buttonLabel = block.label;
    ariaLabel = block.label;
  }

  // Only genuinely-blocked states disable the button. The "needs access" CTA
  // stays enabled so the visitor can act on it; a normal booking still requires
  // a valid selection (canContinue).
  const actionDisabled =
    isCreatingBooking || block != null || (!needsAccess && !canContinue);

  return (
    <TicketCard
      borderColor="var(--theme-100)"
      punchRadius="1.3rem"
      className={className}
    >
      {/* Top Section - Reusable BookingSummaryDisplay */}
      <BookingSummaryDisplay
        bayName={bayName}
        date={formattedDate}
        timeRange={timeRange}
        duration={formatDuration(durationMinutes)}
        guestCount={guestCount}
        premiumNote={premiumNote}
        peakNote={isDevelopment && isPeakRate ? 'Peak Pricing' : undefined}
        promoCode={promotion?.applied ? promotion.code : undefined}
      />

      {/* Punch hole divider - only show when there's content below */}
      {(showPricing || onContinue || onConfirmBooking) && (
        <TicketDivider punch />
      )}

      {/* Bottom Section - Page-specific actions */}
      <div 
      className="p-6"
      data-testid="booking-ticket-card-actions">
        {/* Mobile Layout */}
        <div className="flex flex-col gap-6 md:hidden">
          {/* Price Section */}
          {showPricing && calculatedPrice !== null && !isFree && (
            <PriceDisplay price={calculatedPrice} showTaxDisclaimer={!priceIncludesTax} />
          )}

        </div>

        {/* Mobile floating CTA — portaled to <body> so the TicketCard's CSS
            mask can't clip it. A mask makes the card the containing block for
            fixed descendants, which trapped the button inside the ticket shape. */}
        {floatingMobileCta && (onContinue || onConfirmBooking) && (
          <TicketCtaButton
            variant="floating-mobile"
            label={buttonLabel}
            ariaLabel={ariaLabel}
            onClick={handleAction}
            disabled={actionDisabled}
            busy={isCreatingBooking}
            busyLabel={isFree ? 'Creating Booking...' : 'Processing...'}
          />
        )}

        {/* Desktop Layout */}
        <div 
        className="hidden md:flex md:items-center md:justify-end md:gap-6" 
        data-testid="booking-ticket-actions-desktop"
        >
          {/* Price Section */}
          {showPricing && calculatedPrice !== null && !isFree && (
            <PriceDisplay price={calculatedPrice} showTaxDisclaimer={!priceIncludesTax} />
          )}

          {/* Continue Button */}
          {(onContinue || onConfirmBooking) && (
            <TicketCtaButton
              variant="desktop"
              label={buttonLabel}
              ariaLabel={ariaLabel}
              onClick={handleAction}
              disabled={actionDisabled}
              busy={isCreatingBooking}
              busyLabel={isFree ? 'Creating...' : 'Processing...'}
            />
          )}
        </div>

        {/* Block explanation (e.g. not enrolled at this club) */}
        {block && (
          <p
            className="mt-4 text-center text-sm text-fg-quaternary md:text-right"
            role="status"
            data-testid="booking-ticket-blocked-message"
          >
            {block.message}
          </p>
        )}
      </div>
    </TicketCard>
  );
}
